import {
  getListCompaniesKey,
  ListCompaniesPathParameter,
  ListCompaniesRequest,
  ListCompaniesResponse,
} from "@/features/line-sheet-sets/hooks/use-list-companies";
import useSWR, { SWRConfiguration } from "swr";
import useFetcher from "@/utils/use-fetcher";
import { AxiosRequestConfig } from "axios";
import { Company } from "@/features/types";
import { useMemo } from "react";
import {
  calcUrl,
  withListAllFetcher,
} from "@/features/ui/helpers/fetcher-helpers";
import { UrlParser } from "@/features/ui/helpers/url-parser";
import { StringTransformer } from "@/features/ui/helpers/string-transformer";

export interface ListAllCompaniesRequest
  extends Omit<ListCompaniesRequest, "pageSize" | "pageNumber"> {}
type ListAllCompaniesResponse = Company[];
export interface ListAllCompaniesPathParameter
  extends ListCompaniesPathParameter {}

export const listAllCompaniesTemplateUrl = `/:by/:companyId/companies/list-all`;

export function getListAllCompaniesKey(
  parameter?: ListAllCompaniesPathParameter,
  request?: ListAllCompaniesRequest
) {
  if (parameter && request) {
    return {
      url: calcUrl(listAllCompaniesTemplateUrl, parameter),
      params: request,
    };
  }
  return null;
}

export function parseListAllCompaniesKey(
  key: AxiosRequestConfig<ListAllCompaniesRequest>
) {
  const urlParser = new UrlParser<ListAllCompaniesPathParameter>(
    listAllCompaniesTemplateUrl,
    {
      by: StringTransformer.parseBy,
      companyId: StringTransformer.parseId,
    }
  );
  return {
    parameter: urlParser.parse(key.url!),
    request: key.params,
  };
}

export default function useListAllCompanies(
  parameter?: ListAllCompaniesPathParameter,
  request?: ListAllCompaniesRequest,
  config: SWRConfiguration = { revalidateOnFocus: false, revalidateIfStale: false }
) {
  const listFetcher = useFetcher<ListCompaniesRequest, ListCompaniesResponse>();
  const fetcher = useMemo(() => {
    return withListAllFetcher(
      (config: AxiosRequestConfig<ListCompaniesRequest>) => {
        const { parameter, request } = parseListAllCompaniesKey(config);
        return listFetcher(getListCompaniesKey(parameter, request)!);
      },
      (response) => {
        return response.companies;
      }
    );
  }, [listFetcher]);

  return useSWR(getListAllCompaniesKey(parameter, request), fetcher, config);
}
